import { useMemo } from "react";
import type { Nullish } from "src/types/nullish";
import type { Option } from "src/types/option";

/*
 * Some specific `Option` types may have extra fields like `category`, `color`, etc.
 * This utility assumes the option type conforms to the base `Option<TValue>` shape,
 * and will return the same type passed in (including additional fields).
 */

export type OptionCreator<TValue, TOption extends Option<TValue>> = (value: TValue) => TOption;

export function withCurrentOption<TValue, TOption extends Option<TValue>>(
  options: TOption[],
  currentValue: Nullish<TValue>,
  createOption: OptionCreator<TValue, TOption>,
): TOption[] {
  if (!currentValue) return options;
  if (options.some(option => option.value === currentValue)) return options;
  return [...options, createOption(currentValue)];
}

/**
 * React hook that returns a list of options, ensuring the currently selected value
 * is present in the list—even if it wasn't originally included.
 *
 * This is useful for dropdowns or select components where the server may provide a
 * currently-selected value that is valid, but not part of the client-defined list
 * of user-settable options. If the current value is not `null`/`undefined` and not
 * already present in the list, it will be appended using the provided `createOption` function.
 *
 * @template TValue - The type of the option value (usually `string` or `number`)
 * @template TOption - A subtype of `Option<TValue>` representing the full shape of the dropdown option
 *
 * @param options - An array of user-settable options
 * @param currentValue - The currently selected value, which may or may not be included in `options`
 * @param createOption - A function that creates an `Option` object from a raw value
 *
 * @returns A memoized list of options that includes the current value if it wasn't already present
 *
 * @example
 * const currentActionOptions = useOptionsWithCurrent(userSettableActionOptions, currentAction, createActionObject);
 */
export function useOptionsWithCurrent<TValue, TOption extends Option<TValue>>(
  options: TOption[],
  currentValue: Nullish<TValue>,
  createOption: OptionCreator<TValue, TOption>,
): TOption[] {
  const memoized = useMemo(
    () => withCurrentOption(options, currentValue, createOption),
    [createOption, currentValue, options],
  );
  return memoized;
}
